import { useId, useState } from 'react'

const SAVED_MINUTES_FLOOR = 5

const formatCurrency = (n: number) => `$${Math.round(n).toLocaleString()}`
const formatNumber = (n: number) => Math.round(n).toLocaleString()

const licenceForVolume = (lims: number): number => {
  if (lims < 500) return 13_100
  if (lims < 1_500) return 15_900
  if (lims < 4_000) return 20_100
  if (lims < 7_000) return 24_200
  return 28_400
}

const formatPayback = (months: number): string => {
  if (!Number.isFinite(months) || months <= 0) return '—'
  if (months < 1) return '< 1'
  return String(Math.round(months))
}

type SliderProps = {
  label: string
  help: string
  value: number
  min: number
  max: number
  step: number
  format: (n: number) => string
  onChange: (n: number) => void
}

const Slider = ({ label, help, value, min, max, step, format, onChange }: SliderProps) => {
  const id = useId()
  const helpId = `${id}-help`
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-semibold text-brand-navy">
          {label}
        </label>
        <span aria-hidden="true" className="text-base font-semibold text-brand-blue tabular-nums">
          {format(value)}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-describedby={helpId}
        aria-valuetext={format(value)}
        className="w-full accent-brand-blue cursor-pointer"
      />
      <p id={helpId} className="text-xs text-brand-navy/60 m-0">
        {help}
      </p>
    </div>
  )
}

const SavingsCalculator = () => {
  const [lims, setLims] = useState(2_000)
  const [minutesPerLim, setMinutesPerLim] = useState(45)
  const [hourlyCost, setHourlyCost] = useState(90)

  const hoursSaved = (lims * Math.max(0, minutesPerLim - SAVED_MINUTES_FLOOR)) / 60
  const costSaved = hoursSaved * hourlyCost
  const licence = licenceForVolume(lims)
  const monthsToPayback = costSaved > 0 ? licence / (costSaved / 12) : Infinity

  return (
    <div className="rounded-lg bg-neutral-muted overflow-hidden">
      <div className="p-6 lg:p-8 space-y-6">
        <div className="space-y-5">
          <Slider
            label="LIMs issued per year"
            help="Your council's annual LIM volume."
            value={lims}
            min={100}
            max={8_000}
            step={100}
            format={(n) => n.toLocaleString()}
            onChange={setLims}
          />
          <Slider
            label="Current time per LIM Hazard Section Report"
            help="Time your team currently spends compiling natural hazard information per LIM."
            value={minutesPerLim}
            min={15}
            max={90}
            step={1}
            format={(n) => `${n} min`}
            onChange={setMinutesPerLim}
          />
          <Slider
            label="Loaded labour cost per hour"
            help="Including salary, overheads, and oncosts. $90 is a typical NZ council average."
            value={hourlyCost}
            min={60}
            max={150}
            step={5}
            format={(n) => `$${n}`}
            onChange={setHourlyCost}
          />
        </div>

        <div className="border-0 border-t border-solid border-neutral-charcoal/15 pt-6">
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="space-y-5 tabular-nums"
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-navy/60 m-0">
                Hours saved per year
              </p>
              <p className="text-2xl font-semibold text-brand-navy m-0 mt-1">
                {formatNumber(hoursSaved)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-navy/60 m-0">
                Staff cost saved per year
              </p>
              <p className="text-5xl font-light text-brand-blue m-0 mt-1 leading-none">
                {formatCurrency(costSaved)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-navy/60 m-0">
                Months to payback on annual licence
              </p>
              <p className="text-2xl font-semibold text-brand-navy m-0 mt-1">
                {formatPayback(monthsToPayback)}
                <span className="text-base font-normal text-brand-navy/60 ml-2">
                  on a {formatCurrency(licence)} licence
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SavingsCalculator
